import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as ToastPrimitive from '@radix-ui/react-toast';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useRef, useState, } from 'react';
import { cn } from '../lib/cn.js';
export const TOAST_TONE = {
    SUCCESS: 'SUCCESS',
    ERROR: 'ERROR',
    INFO: 'INFO',
};
const ToastContext = createContext(null);
const DEFAULT_DURATION_MS = 5000;
/** Radix treats Infinity as "never auto-dismiss". */
const NEVER = Number.POSITIVE_INFINITY;
/**
 * Toast notifications.
 *
 * Two rules that are not Radix defaults.
 *
 * **An error toast does not disappear on its own.** A success message the user
 * misses costs nothing; a failure they miss means they believe a change was
 * saved when it was not. Errors stay until dismissed.
 *
 * **A toast is never the only report of a failure.** It is an announcement,
 * not a record: it is gone in five seconds, it cannot be re-read, and it is
 * the wrong place for anything a user needs while they act. Form errors belong
 * next to the form. This is why `FormDialog` renders its own error rather than
 * calling `toast`.
 */
export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const nextId = useRef(0);
    const dismiss = useCallback((id) => {
        setToasts((current) => current.filter((entry) => entry.id !== id));
    }, []);
    const toast = useCallback((options) => {
        nextId.current += 1;
        const id = `toast-${String(nextId.current)}`;
        setToasts((current) => [...current, { ...options, id }]);
    }, []);
    const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);
    return (_jsx(ToastContext.Provider, { value: value, children: _jsxs(ToastPrimitive.Provider, { swipeDirection: "right", children: [children, toasts.map((entry) => (_jsx(ToastItem, { record: entry, onDismiss: () => {
                        dismiss(entry.id);
                    } }, entry.id))), _jsx(ToastPrimitive.Viewport, { className: "fixed bottom-0 right-0 z-toast flex w-full max-w-sm flex-col gap-2 p-4" })] }) }));
}
const TONE_ICON = {
    [TOAST_TONE.SUCCESS]: CheckCircle2,
    [TOAST_TONE.ERROR]: AlertCircle,
    [TOAST_TONE.INFO]: Info,
};
const TONE_CLASS = {
    [TOAST_TONE.SUCCESS]: 'border-success/40 text-success-foreground',
    [TOAST_TONE.ERROR]: 'border-destructive/40 text-destructive',
    [TOAST_TONE.INFO]: 'border-info/40 text-info-foreground',
};
function ToastItem({ record, onDismiss, }) {
    const tone = record.tone ?? TOAST_TONE.INFO;
    const Icon = TONE_ICON[tone];
    const duration = record.durationMs ?? (tone === TOAST_TONE.ERROR ? NEVER : DEFAULT_DURATION_MS);
    return (_jsxs(ToastPrimitive.Root, { open: true, duration: duration, onOpenChange: (open) => {
            if (!open)
                onDismiss();
        }, 
        // An error is assertive: it interrupts, because the user is about to act
        // on the belief that something worked. Anything else is polite.
        type: tone === TOAST_TONE.ERROR ? 'foreground' : 'background', className: cn('flex items-start gap-3 rounded-md border bg-background p-4 shadow-lg', TONE_CLASS[tone]), children: [_jsx(Icon, { className: "mt-0.5 h-4 w-4 shrink-0", "aria-hidden": "true" }), _jsxs("div", { className: "flex-1 space-y-1", children: [_jsx(ToastPrimitive.Title, { className: "text-sm font-medium", children: record.title }), record.description === undefined ? null : (_jsx(ToastPrimitive.Description, { className: "text-sm text-muted-foreground", children: record.description }))] }), _jsx(ToastPrimitive.Close, { "aria-label": "Dismiss", className: "shrink-0 opacity-70 hover:opacity-100", children: _jsx(X, { className: "h-4 w-4", "aria-hidden": "true" }) })] }));
}
export function useToast() {
    const context = useContext(ToastContext);
    if (context === null) {
        throw new Error('useToast must be used inside a <ToastProvider>');
    }
    return context;
}
//# sourceMappingURL=toast.js.map